import { ref, Ref } from 'vue';
import { watchDebounced } from '@vueuse/core';
import { Loader } from '@googlemaps/js-api-loader';

interface Params {
  debounceTimer?: number;
  blockSearch?: Ref<boolean>;
}

interface Address {
  address1: string;
  address2: string;
  city: string;
  zip: string;
  country: string;
  countryCode: string;
  region: string;
  regionCode: string;
  company: string;
  latitude: number | null;
  longitude: number | null;
}

let sessionToken: google.maps.places.AutocompleteSessionToken | null = null;

const useGoogleAutoComplete = ({ debounceTimer = 300, blockSearch }: Params = {}) => {
  const text = ref<string>('');
  const suggestionList = ref<google.maps.places.AutocompletePrediction[]>([]);
  const selectedAddress = ref<Address | null>(null);
  let autocompleteService: google.maps.places.AutocompleteService | null = null;
  let placesService: google.maps.places.PlacesService | null = null;

  const retrieve = async (placeId: string): Promise<Address | null> => {
    if (!placesService) return null;

    return new Promise((resolve, reject) => {
      placesService!.getDetails(
        {
          placeId,
          fields: ['address_components', 'geometry', 'name'],
          sessionToken: sessionToken || '',
        },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            resolve(mapPlaceToAddress(place));
          } else {
            reject(status);
          }
        }
      );
    });
  };

  const find = async (input: string) => {
    if (!autocompleteService) return null;

    return new Promise<google.maps.places.AutocompletePrediction[]>((resolve, reject) => {
      autocompleteService!.getPlacePredictions(
        { input, componentRestrictions: { country: 'uk' }, sessionToken: sessionToken || '' },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            resolve(predictions);
          } else {
            reject(status);
          }
        }
      );
    });
  };

  const mapPlaceToAddress = (place: google.maps.places.PlaceResult): Address => {
    const address: Address = {
      address1: '',
      address2: '',
      city: '',
      zip: '',
      country: '',
      countryCode: '',
      region: '',
      regionCode: '',
      company: '',
      latitude: null,
      longitude: null,
    };

    if (place.address_components) {
      for (const component of place.address_components) {
        const types = component.types;
        if (types.includes('street_number')) {
          address.address1 = component.long_name + ' ' + address.address1;
        }
        if (types.includes('route')) {
          address.address1 += component.long_name;
        }
        if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
          address.address2 = component.long_name;
        }
        if (types.includes('locality')) {
          address.city = component.long_name;
        }
        if (types.includes('postal_code')) {
          address.zip = component.long_name;
        }
        if (types.includes('country')) {
          address.country = component.long_name;
          address.countryCode = component.short_name;
        }
        if (types.includes('administrative_area_level_1')) {
          address.region = component.long_name;
          address.regionCode = component.short_name;
        }
      }
    }

    if (place.geometry && place.geometry.location) {
      address.latitude = place.geometry.location.lat();
      address.longitude = place.geometry.location.lng();
    }

    if (place.name) {
      address.company = place.name;
    }

    return address;
  };

  watchDebounced(
    text,
    async queryValue => {
      if (!blockSearch?.value) {
        if (queryValue) {
          try {
            const foundItems = await find(queryValue);
            if (queryValue === text.value && foundItems) {
              suggestionList.value = foundItems;
            }
          } catch (error) {
            console.error('Error finding addresses:', error);
          }
        } else {
          suggestionList.value = [];
        }
      }
    },
    { debounce: debounceTimer }
  );

  const reset = () => {
    text.value = '';
    suggestionList.value = [];
  };

  const loadGoogleMaps = () => {
    const loader = new Loader({
      apiKey: import.meta.env.VITE_GOOGLE_PLACES_KEY,
      libraries: ['places'],
    });

    loader.importLibrary('places').then(() => {
      autocompleteService = new google.maps.places.AutocompleteService();
      const map = new google.maps.Map(document.createElement('div')); // Required to initialize placesService
      placesService = new google.maps.places.PlacesService(map);
      if (!sessionToken) {
        sessionToken = new google.maps.places.AutocompleteSessionToken();
      }
    });
  };

  onMounted(() => {
    loadGoogleMaps();
  });

  return {
    retrieve,
    find,
    text,
    suggestionList,
    selectedAddress,
    reset,
  };
};

export default useGoogleAutoComplete;
